// Blog content lives here as plain data so every post is prerendered into static HTML at build time
// (good for Google, and for AI assistants that read pages without running JavaScript).
// To add a post: append an object below, then run `npm run build` (sitemap, RSS and llms.txt update themselves).

const RAW_POSTS = [
  {
    slug: 'what-is-generative-engine-optimization-geo-aeo-seo',
    title: 'What Is GEO? Generative Engine Optimization, AEO and SEO Explained',
    description:
      'SEO gets you ranked, AEO gets you the featured answer, GEO gets you cited by ChatGPT, Gemini and Perplexity. Here is how the three fit together and what to do first.',
    date: '2026-10-20',
    updated: '2026-10-22',
    category: 'SEO & AI Search',
    tags: ['GEO', 'AEO', 'SEO', 'AI search', 'llms.txt'],
    service: 'custom-web-development-company',
    summary:
      'SEO helps your pages rank in Google. AEO (answer engine optimization) structures pages so search engines can lift a direct answer. GEO (generative engine optimization) makes your brand easy for AI assistants such as ChatGPT, Gemini, Claude and Perplexity to understand, trust and cite. A good site does all three with the same foundation: fast pages, clear answers, structured data and consistent facts about your business.',
    faq: [
      {
        q: 'What is the difference between SEO, AEO and GEO?',
        a: 'SEO earns rankings in a list of search results. AEO earns the single direct answer shown above or instead of that list, such as featured snippets and voice answers. GEO earns a mention or citation inside an answer written by an AI assistant. They overlap heavily, and the same clear, structured content serves all three.',
      },
      {
        q: 'Do I need an llms.txt file?',
        a: 'It is not required and no AI company has promised to rank by it, but it is cheap to publish and gives AI tools a clean, plain-text summary of who you are, what you do and which pages matter. We add one to every site we build.',
      },
      {
        q: 'Can I track whether AI assistants mention my business?',
        a: 'Yes, in a simple way: ask the same set of customer questions in ChatGPT, Gemini, Perplexity and Claude each month and record whether your brand appears and whether the facts are correct. Also check your analytics for referral traffic from those tools.',
      },
    ],
    content: `
## SEO, AEO and GEO in one minute

People now find businesses in three different ways, and each has its own name:

* **SEO (search engine optimization)** is about ranking your pages in Google and Bing results.
* **AEO (answer engine optimization)** is about being the direct answer: the featured snippet, the "People also ask" box, the voice assistant reply.
* **GEO (generative engine optimization)** is about being included in answers that AI assistants write, such as ChatGPT, Gemini, Claude, Perplexity and Google AI Overviews.

You do not need three separate strategies. They share one foundation, and most of the work is making your site clear to machines and useful to people.

## How do AI assistants decide what to mention?

Nobody outside the AI companies knows the exact formula, but the patterns are consistent. Assistants tend to cite pages that:

1. **Answer the question directly**, near the top of the page, in plain language.
2. **Are easy to read for a machine**, with clean headings, lists and structured data (JSON-LD).
3. **State facts consistently** across your site, your Google Business Profile and your social profiles: name, services, location, contact details.
4. **Load fast and are crawlable**, with server-rendered HTML rather than a blank page that needs JavaScript to appear.
5. **Come from a source that looks trustworthy**: real authors, real projects, real reviews, and other sites that mention you.

## The practical checklist

### 1. Make sure crawlers can read your pages
Single-page apps that render only in the browser can look empty to many crawlers. Pre-render or server-render your pages so the full text is in the HTML. All Farwings sites are built this way.

### 2. Write answer-first content
Start each page or article with a two or three sentence answer, then expand. Use question-style headings such as "How much does it cost?" or "How long does it take?" because that is how people and assistants ask.

### 3. Add structured data
Use JSON-LD for your Organization, your Services, your Articles and a FAQPage on pages that really contain questions and answers. Structured data removes guesswork about what a page is.

### 4. Publish robots.txt, a sitemap and llms.txt
* **robots.txt** should not accidentally block search and AI crawlers you want, such as Googlebot, Bingbot, OAI-SearchBot, ClaudeBot and PerplexityBot.
* **sitemap.xml** lists every page and when it last changed.
* **llms.txt** is a short markdown file at the root of your site that summarises your business and links to your key pages for AI tools.

### 5. Keep your facts consistent
If your website says you are in Ahmedabad but an old directory says another city, assistants may repeat either. Audit your name, address, phone number and service list everywhere.

### 6. Earn mentions elsewhere
Case studies, client reviews, guest articles, directory listings and social profiles all help assistants confirm that you are a real, active business.

## What not to do

Do not stuff pages with keywords, publish thin AI-generated articles in bulk, or invent statistics. Search engines and AI assistants both reward accuracy, and low-quality pages can hurt trust in your whole domain.

## Where to start

If you only have an afternoon: fix your page titles and descriptions, add an answer-first summary to your top five pages, add FAQ sections where you genuinely get those questions, and publish a sitemap and llms.txt. If you would like help, our [custom web development](/services/custom-web-development-company) service builds all of this in from day one, or you can [contact us](/contact) for an SEO review of your current site.
`,
  },
  {
    slug: 'website-development-cost-india-what-affects-the-price',
    title: 'Website Development Cost in India: What Really Affects the Price',
    description:
      'What decides the cost of a website or web app in India: scope, design, integrations, content and maintenance. A clear checklist to get an accurate fixed quote.',
    date: '2026-10-12',
    updated: '2026-10-22',
    category: 'Web Development',
    tags: ['website cost', 'web development India', 'pricing', 'quotation'],
    service: 'custom-web-development-company',
    summary:
      'There is no single price for a website in India. A simple brochure site, an online store and a custom web application sit at very different points because cost follows scope: number of pages and features, custom design, integrations, content, and ongoing maintenance. The fastest way to an accurate number is to write down what the site must do and ask for a fixed-scope quotation.',
    faq: [
      {
        q: 'Why do website quotes vary so much between agencies?',
        a: 'Because the scope is often unclear. One quote may include custom design, SEO setup, content and a year of support while another covers only a template. Always compare what is included, not just the total.',
      },
      {
        q: 'Is a cheaper template site good enough?',
        a: 'For a simple brochure site, often yes. If you need custom workflows, user accounts, payments, dashboards or integrations with other software, a template quickly becomes a limit and custom development is usually better value over time.',
      },
      {
        q: 'Are there ongoing costs after launch?',
        a: 'Yes. Plan for hosting, a domain, security updates, backups and small improvements. Many businesses also budget for SEO content and occasional new features.',
      },
    ],
    content: `
## What drives the cost of a website?

Website cost follows scope. These are the main factors, roughly in order of impact.

### 1. Type of project
A brochure website, an online store, a booking platform and a SaaS product are very different amounts of work. The more logic the site contains (accounts, payments, dashboards, roles, reports), the more design, development and testing it needs.

### 2. Number and complexity of pages
Five clean pages are quick. Fifty pages with filters, search and dynamic content take longer. Reusable page templates keep this under control.

### 3. Custom design versus a template
A template is faster and cheaper but looks like other sites. Custom UI and UX design takes more time and lets your brand and conversion goals shape every screen.

### 4. Integrations
Payment gateways, WhatsApp, CRM, ERP, accounting software, maps, SMS and email each add work. Integrations with older or poorly documented systems cost the most.

### 5. Content and media
Copywriting, product data entry, photography and animation are often forgotten in early budgets. Decide early who provides them.

### 6. SEO, speed and security
Technical SEO, fast load times, secure forms and correct structured data should be built in, not added later. They take planning but pay back through traffic and trust.

### 7. Timeline
A rushed deadline usually raises the price because work cannot be scheduled efficiently.

### 8. Maintenance
Hosting, backups, updates and support continue after launch. A clear support plan avoids surprises.

## How do I get an accurate quote?

Write a one-page brief before you contact anyone:

1. What is the business goal of the site?
2. Who are the users and what must they be able to do?
3. A list of must-have and nice-to-have features.
4. Sites you like, and sites you do not.
5. Your deadline and rough budget range.
6. What content you already have.

With this, a good agency can give a fixed-scope quotation with a clear list of what is included. At Farwings we send a written quotation with scope, timeline and milestones before any work begins. See our [services](/services) or [request a quote](/contact).

## Red flags to watch for

* A price with no written scope.
* No mention of testing, SEO or security.
* Ownership of the code and domain staying with the agency.
* No plan for what happens after launch.
`,
  },
  {
    slug: 'react-vs-nextjs-which-is-right-for-your-business-website',
    title: 'React vs Next.js: Which Is Right for Your Business Website?',
    description:
      'React or Next.js? A plain-language comparison covering SEO, speed, cost and when each framework makes sense for a business website or web app.',
    date: '2026-09-28',
    updated: '2026-10-22',
    category: 'Web Development',
    tags: ['React', 'Next.js', 'web development', 'SEO'],
    service: 'custom-web-development-company',
    summary:
      'React is a library for building user interfaces. Next.js is a framework built on React that adds routing, server rendering and static generation. For public websites that need to rank in search, Next.js (or a React setup with static pre-rendering) is usually the better choice. For logged-in dashboards where SEO does not matter, plain React with Vite is simpler and fast.',
    faq: [
      {
        q: 'Is Next.js better than React for SEO?',
        a: 'Next.js makes good SEO easier because pages are rendered on the server or at build time, so crawlers receive full HTML. A plain React app can match it only if you add pre-rendering.',
      },
      {
        q: 'Is Next.js harder to maintain?',
        a: 'It has more conventions and moving parts than a plain React app, but it removes the need to assemble routing, rendering and image handling yourself. For most business sites the trade is worth it.',
      },
      {
        q: 'Can I switch from React to Next.js later?',
        a: 'Yes. Because Next.js is built on React, components are largely reusable, so migration is usually a restructuring of routing and data loading rather than a rewrite.',
      },
    ],
    content: `
## React and Next.js are not rivals

React is the library that draws your interface from components. Next.js is a framework that uses React and adds the things a production website needs: file-based routing, server-side rendering (SSR), static site generation (SSG), image optimization and API routes.

So the real question is not "React or Next.js?" but "Do I need what Next.js adds?"

## When Next.js is the better choice

* **Marketing sites, blogs and service pages** that must rank on Google. Server-rendered or pre-rendered HTML gives crawlers the full content immediately.
* **E-commerce** where product pages need to be fast and indexable.
* **Sites with a lot of pages** generated from data, such as locations, products or articles.
* **Projects that want one codebase** for the website and its backend endpoints.

## When plain React (with Vite) is enough

* **Internal dashboards and admin panels** behind a login, where search engines never visit.
* **Highly interactive tools** such as editors, calculators and visualisations.
* **Prototypes** that need to move quickly.

## Can plain React rank well?

Yes, if you pre-render. This very website is built with React and static generation, so every page is real HTML at build time and still gets React's interactivity afterwards. The point is that the HTML must exist before JavaScript runs.

## Speed and Core Web Vitals

Both can be very fast when built carefully. What matters most is shipping less JavaScript, optimizing images and fonts, loading heavy parts on demand, and hosting on a good CDN. Next.js includes helpers for several of these, but discipline matters more than the tool.

## Cost and maintenance

Next.js adds some learning curve, but it saves time on routing, rendering and SEO plumbing. For a business website the total cost is usually similar, and the result is easier to find on search and AI assistants.

## Our recommendation

Public site that needs traffic: Next.js, or React with static generation. Private app: React with Vite. If you are unsure, [talk to us](/contact) and we will recommend the simplest option that meets your goals. You can also read about our [custom web development](/services/custom-web-development-company) process.
`,
  },
  {
    slug: 'how-custom-software-development-can-tailor-your-business-processes',
    title: 'How Custom Software Development Can Tailor Your Business Processes',
    description:
      'Why off-the-shelf software starts to hold growing businesses back, and how custom software development fits your exact workflow, integrations and security needs.',
    date: '2023-10-15',
    updated: '2026-10-22',
    category: 'Custom Software',
    tags: ['custom software', 'business process', 'automation', 'ERP'],
    service: 'custom-software-development',
    summary:
      'Custom software is built around the way your business actually works, instead of forcing your team to adapt to generic tools. It is worth considering when you rely on spreadsheets and workarounds, pay for features you never use, or need to connect systems that do not talk to each other.',
    faq: [
      {
        q: 'When is custom software better than off-the-shelf software?',
        a: 'When your process is a competitive advantage, when you outgrow generic tools, or when you need several systems to work together. For common needs such as basic email or accounting, ready-made tools are usually enough.',
      },
      {
        q: 'How long does custom software take to build?',
        a: 'A focused first version often takes weeks rather than months. We usually start with a small core release, then add features in stages so you see value early.',
      },
      {
        q: 'Who owns the code?',
        a: 'You should. Agree on code ownership, documentation and handover in writing before the project starts.',
      },
    ],
    content: `
## The problem with off-the-shelf software

Many businesses start with generic tools, and that is the right call at first. As you grow, though, you begin changing your process to suit the software rather than the other way round. Teams copy data between systems, keep side spreadsheets, and pay for features they never use.

## What custom software development gives you

Custom software is designed around your workflow. Before writing code we map your steps, roles, approvals and edge cases, so the finished product matches how your team really works.

### Key benefits

* **Fit:** every screen and report exists because your team needs it.
* **Scalability:** cloud-ready architecture on platforms such as AWS and Docker grows with your volume.
* **Security:** role-based access, secure APIs and audit trails protect sensitive data.
* **Integration:** connect your CRM, accounting, WhatsApp, payment and legacy systems in one flow.
* **Ownership:** no per-seat licence surprises, and you control the roadmap.

## Signs it is time

1. Staff re-enter the same data in more than one place.
2. Reports take hours to prepare by hand.
3. Your process has steps that no tool supports.
4. Licence costs rise faster than the value you get.

## How we approach it

We start with discovery, build a small first release, test it with real users and then extend it. Examples include delivery management platforms, billing and inventory systems and customer portals. Read more about our [custom software development](/services/custom-software-development) service, or [get in touch](/contact) to discuss your workflow.
`,
  },
  {
    slug: 'maximizing-your-seo-efforts-for-voice-search-optimization',
    title: 'Voice Search, AI Answers and SEO: How to Get Found in 2026',
    description:
      'Optimize for voice search and AI answers: conversational keywords, question-style headings, FAQ and JSON-LD schema, and fast, crawlable pages.',
    date: '2023-11-02',
    updated: '2026-10-22',
    category: 'SEO & AI Search',
    tags: ['voice search', 'SEO', 'AEO', 'schema markup', 'AI Overviews'],
    service: 'custom-web-development-company',
    summary:
      'Voice assistants and AI tools answer questions in full sentences, so pages that win are written the same way: a direct answer first, question-style headings, FAQ content, structured data and very fast load times.',
    faq: [
      {
        q: 'What is voice search optimization?',
        a: 'It is writing and structuring your pages so that voice assistants and AI answer engines can read out or summarise your content as the answer to a spoken question.',
      },
      {
        q: 'Does schema markup help voice search?',
        a: 'It helps search engines understand what your page is, which supports featured snippets and rich results. It does not guarantee a spot, but it removes ambiguity.',
      },
      {
        q: 'How long should an answer be?',
        a: 'For a direct answer, about 40 to 60 words works well. Follow it with detail for readers who want more.',
      },
    ],
    content: `
## From keywords to questions

People speak to assistants the way they speak to a person: "Which is the best app development company in Ahmedabad?" Tools such as ChatGPT, Gemini, Perplexity and Google AI Overviews respond with written answers instead of ten blue links. Your content needs to be easy to quote.

## Strategies that work

### Use conversational, question-based keywords
Look at the questions customers actually ask on calls and WhatsApp, and answer them on your pages with the question as the heading.

### Put the answer first
Give a direct answer in the first two or three sentences, then explain. This is how featured snippets and AI summaries are usually built.

### Add structured data
Use JSON-LD for your organization, services, articles and FAQ so search engines can identify entities and facts instantly.

### Keep pages fast and crawlable
Sub-second perceived load time, mobile-first layout and server-rendered HTML help both users and crawlers.

### Keep your local facts consistent
Your name, address, phone and services should match across your website, Google Business Profile and directories.

## A simple FAQ pattern

Add three to six real questions to key pages, each with a short, accurate answer. Mark them up with FAQPage schema only when the questions and answers are visible on the page.

## Measure it

Test your target questions monthly in voice assistants and AI tools, and watch Search Console for question-style queries. For the bigger picture, read our guide to [GEO, AEO and SEO](/blog/what-is-generative-engine-optimization-geo-aeo-seo).
`,
  },
  {
    slug: 'elevating-your-it-infrastructure-farwings-tech-solutions-a-comprehensive-guide-for-business-optimization',
    title: 'Elevating Your IT Infrastructure: A Practical Guide for Business Optimization',
    description:
      'How to modernize legacy IT: audit your systems, move to the cloud, build secure APIs and adopt React, Next.js and Node.js without disrupting your business.',
    date: '2023-12-10',
    updated: '2026-10-22',
    category: 'Cloud & Infrastructure',
    tags: ['IT infrastructure', 'cloud migration', 'legacy systems', 'APIs'],
    service: 'custom-web-development-company',
    summary:
      'Modernizing IT means auditing what you have, moving data and workloads to scalable cloud services, wrapping old systems in secure APIs and rebuilding the parts that slow you down on modern frameworks, in stages so the business keeps running.',
    faq: [
      {
        q: 'Do I have to replace all my old systems at once?',
        a: 'No. A staged approach is safer: stabilise first, expose old systems through APIs, and replace modules one at a time.',
      },
      {
        q: 'Is the cloud always cheaper?',
        a: 'Not automatically. It is more flexible and easier to scale, but costs need monitoring. Right-sizing and sensible architecture keep them predictable.',
      },
      {
        q: 'How do you avoid downtime during migration?',
        a: 'By running old and new systems in parallel, migrating data in tested batches and switching over during a planned low-traffic window.',
      },
    ],
    content: `
## The hidden cost of technical debt

Outdated systems drain time and money. They are slow to change, hard to secure and difficult to hire for. Each workaround makes the next change harder.

## A practical modernization path

1. **Discovery and audit.** List your systems, data flows, owners and pain points. Find the bottlenecks that cost the most.
2. **Stabilise.** Backups, monitoring and security patches first.
3. **Cloud migration.** Move databases and workloads to scalable services such as AWS and managed PostgreSQL.
4. **API layer.** Wrap legacy systems in secure, documented APIs so new apps can use them.
5. **Rebuild in stages.** Replace the slowest or riskiest modules with React, Next.js and Node.js services.
6. **Measure.** Track speed, errors and cost so you can see the improvement.

## Technology choices

Modern frameworks give you faster interfaces, easier hiring and a larger ecosystem. Containers (Docker) make deployments repeatable. A CDN and caching keep pages fast worldwide.

## What good looks like

Fewer manual steps, faster pages, clear access control, and a system your team can change without fear. Learn about our [custom web development](/services/custom-web-development-company) work, or [contact us](/contact) to plan a staged migration.
`,
  },
  {
    slug: 'navigating-digital-transformation-how-farwings-tech-solutions-can-empower-your-business',
    title: 'Navigating Digital Transformation: How to Empower Your Business',
    description:
      'Digital transformation is a change in how you deliver value, not just new tools. Learn the core pillars: web apps, mobile apps, data and automation.',
    date: '2024-01-05',
    updated: '2026-10-22',
    category: 'Strategy',
    tags: ['digital transformation', 'mobile apps', 'web apps', 'automation'],
    service: 'mobile-app-development-company',
    summary:
      'Digital transformation means using technology to change how your business operates and serves customers. Start with one painful process, replace it with a web or mobile tool, measure the result, then repeat.',
    faq: [
      {
        q: 'Where should a small business start with digital transformation?',
        a: 'Pick one process that wastes the most time, such as quotations, order tracking or customer follow-up, and digitise that first.',
      },
      {
        q: 'Do I need a mobile app?',
        a: 'Only if your users or staff benefit from being on the go, or need notifications and offline use. A fast responsive website is enough for many businesses.',
      },
      {
        q: 'How do I know it worked?',
        a: 'Set a measurable goal first, such as hours saved per week, fewer errors or faster response time, and compare after launch.',
      },
    ],
    content: `
## What is digital transformation?

It is the integration of digital technology into all areas of a business, changing how you operate and how you deliver value. The tools matter less than the outcome: less manual work, better decisions and happier customers.

## Core pillars

* **Custom web applications.** Replace paperwork and scattered spreadsheets with fast internal dashboards.
* **Mobile accessibility.** Cross-platform apps built with Flutter or React Native put tools in your team's and customers' hands.
* **Data-driven insight.** A reliable database and clear reports turn day-to-day activity into decisions.
* **Automation and AI.** Let software handle repetitive replies, data entry and follow-ups.

## A simple roadmap

1. Choose the process that hurts most.
2. Define the outcome you want in numbers.
3. Build a small first version.
4. Train the team and collect feedback.
5. Improve, then move to the next process.

## Common mistakes

Trying to change everything at once, buying tools before defining the problem, and skipping staff training. A phased approach with a trusted partner avoids all three.

See how our [mobile app development](/services/mobile-app-development-company) and [AI automation](/services/ai-automation-services) services can support your plan.
`,
  },
  {
    slug: 'building-a-chatbot-for-your-business-a-step-by-step-guide',
    title: 'Building a Custom AI Chatbot for Your Business: A Step-by-Step Guide',
    description:
      'How to build an AI chatbot that answers customers, captures leads and connects to your CRM or WhatsApp: data, model, integration and testing steps.',
    date: '2024-02-20',
    updated: '2026-10-22',
    category: 'AI Automation',
    tags: ['AI chatbot', 'LLM', 'WhatsApp', 'automation', 'lead generation'],
    service: 'ai-automation-services',
    summary:
      'A business AI chatbot answers customer questions from your own information, available around the clock. Building one means collecting your knowledge, choosing a language model, grounding its answers in your data, connecting it to the channels and tools you use, and testing it carefully before launch.',
    faq: [
      {
        q: 'Can a chatbot work on WhatsApp and Instagram?',
        a: 'Yes. With the official business APIs, one chatbot can answer on WhatsApp, Instagram and your website, and hand over to a human when needed.',
      },
      {
        q: 'How do you stop a chatbot from making things up?',
        a: 'Ground it in your own approved content, tell it to say when it does not know, limit what it can answer, and review conversations regularly.',
      },
      {
        q: 'Is customer data safe?',
        a: 'It can be, if you control where data is stored, limit what is sent to third-party models, and follow privacy rules. We design this with you at the start.',
      },
    ],
    content: `
## Why a business needs an AI chatbot

Customers expect quick answers at any hour. A well-built chatbot handles repetitive questions such as pricing, availability, order status and opening hours, collects lead details and passes complex cases to your team. It saves time without replacing the human touch where it matters.

## The development process

### 1. Define the job
Decide what the bot should do: answer FAQs, qualify leads, book appointments or track orders. A narrow, clear scope works better than "answer everything".

### 2. Gather and clean your knowledge
Collect product information, policies, price lists and common questions. Remove outdated items. The answers can only be as good as this source.

### 3. Choose the model and architecture
Use a large language model with retrieval: your content is stored in a vector database, relevant passages are fetched for each question, and the model writes an answer from them. This keeps replies accurate and on-brand.

### 4. Connect your channels and tools
Link the bot to WhatsApp, Instagram, your website and your CRM so conversations and leads are saved automatically.

### 5. Add guardrails and human handover
Set rules for tone, topics and uncertainty. When the bot is not sure, it should say so and offer a human.

### 6. Test, launch and improve
Test with real customer questions, launch to a small group, review transcripts and refine every week.

## What results to expect

Results depend on your volume and how repetitive your questions are. Many businesses see faster response times and fewer routine messages for staff. Define a measurable goal before you start.

Explore our [AI automation services](/services/ai-automation-services) or [talk to us](/contact) about a chatbot for your business.
`,
  },
];

const wordCount = (md) => md.trim().split(/\s+/).length;

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

export const blogPosts = RAW_POSTS.map((p) => ({
  ...p,
  id: p.slug,
  excerpt: p.description,
  published: formatDate(p.date),
  updatedLabel: formatDate(p.updated || p.date),
  readMinutes: Math.max(2, Math.round(wordCount(p.content) / 200)),
})).sort((a, b) => (a.date < b.date ? 1 : -1));

export const getPost = (slug) => blogPosts.find((p) => p.slug === slug);

export const getRelatedPosts = (post, limit = 3) =>
  blogPosts
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({ p, score: p.tags.filter((t) => post.tags.includes(t)).length + (p.category === post.category ? 2 : 0) }))
    .sort((a, b) => b.score - a.score || (a.p.date < b.p.date ? 1 : -1))
    .slice(0, limit)
    .map((x) => x.p);
